import * as Tone from "tone";
import MasterChannel from "components/modules/Music/masterChannel"
import InstrumentBase from "components/modules/Music/instrumentBase";
import Scales from "components/modules/Music/scales";
import Presets from "components/modules/Music/presets";

class MainController {
  constructor(cb = () => {}) {
    this.patternLength = 16;
    this.rows = 5;
    this.notes = [];
    this.currentScale = 'min_penta';
    this.currentRoot = 0; // C
    this.currentOctaves = [3, 3, 3];
    this.sequencerStep = 0;
    this.master = MasterChannel.masterOut;
    this.globalPreset = '';
    this.downloading = false;

    this.tracks = this.setupTracks();

    this.setGlobalPreset('DEEPHOUSE');

    this.masterEffects = [false, false, false];

    this.mutedTracks = [false, false, false];

    this.recorder = null;

    // Our transport
    Tone.Transport.scheduleRepeat((time)=>{
      let step = this.sequencerStep % this.patternLength;
      // Tracks are the boxes (3), loop through each one
      for (let i = 0; i < this.tracks.length; i++) {
        if (!this.mutedTracks[i]) {
          // patterns are the rows (4 or 5)
          for (let j = 0; j < this.tracks[i].pattern.length; j++) {
            if (this.tracks[i].pattern[j][step]) {
              this.tracks[i].instrument.trigger(this.getNoteToPlay(i, j), time);
              // If we're on a monosynth we need to make sure
              // we're not playing two notes at once,
              // so we exit after playing a note
              if (i === 0 || i === 2) {
                break;
              }
            }
          }
        }
      }
      Tone.Draw.schedule(() => {
        cb(step, this.notesAtStep(step));
      }, time);
      this.sequencerStep++;
    }, "16n");
  }

  /**
   * Get notes at step
   * @param  {number} step [the step]
   *
   * @return {array}       [the array of notes at the step]
   */
  notesAtStep(step) {
    let tracks = [[], [], []];
    // Tracks are the boxes (3), loop through each one
    for (let i = 0; i < this.tracks.length; i++) {
      // patterns are the rows (4 or 5)
      for (let j = 0; j < this.tracks[i].pattern.length; j++) {
        if (this.tracks[i].pattern[j][step]) {
          tracks[i].push({
            track: i,
            step: step,
            note: this.getNoteToPlay(i, j),
            pattern: j
          });
          // If we're on a monosynth we need to make sure
          // we're not playing two notes at once,
          // so we exit after playing a note
          if (i === 0 || i === 2) {
            break;
          }
        }
      }
    }
    return tracks;
  }

  /**
   * Setup function
   *
   * @return {array}       [the array of tracks]
   */
  setupTracks() {
    const tracks = [];
    const bass = new InstrumentBase(Presets.masterEffects['bass']);
    tracks.push({
      instrument: bass,
      name: "bass",
      pattern: this.createPattern()
    });
    const drums = new InstrumentBase(Presets.masterEffects['drums']);
    tracks.push({
      instrument: drums,
      name: "drums",
      pattern: this.createPattern()
    });
    const lead = new InstrumentBase(Presets.masterEffects['lead']);
    tracks.push({
      instrument: lead,
      name: "lead",
      pattern: this.createPattern()
    });
    return tracks
  }

  /**
   * Setup function to create pattern
   *
   * @return {array} [the pattern (array of all zeros)]
   */
  createPattern() {
    const pattern = []
    for (let i = 0; i < this.rows; i++) {
      const rows = []
      for (let j = 0; j < this.patternLength; j++) {
        rows.push(0)
      }
      pattern.push(rows)
    }
    return pattern
  }

  /**
   * Update the entire pattern at once,
   * instead of updating individual notes
   *
   * @param  {Number} instrumentId [ID of the instrument]
   * @param  {Array}  array        [The puffs placement]
   */
  updateEntirePattern(instrumentId, array) {
    if (instrumentId !== undefined) {
      for (let i = 0; i < array.length; i++) {
        // changed this so lowest not is the bottom row / Andreas
        const reversed = array[i].reverse()
        for (let j = 0; j < reversed.length; j++) {
          this.tracks[instrumentId].pattern[j][i] = reversed[j];
        }
      }
    }
  }

  /**
   * Update the pattern on an individual note basis
   *
   * @param {int} row
   * @param {int} step
   * @param {boolean} active
   */
  updatePattern(instrumentId, row, step, active) {
    if (instrumentId !== undefined) {
      this.tracks[instrumentId].pattern[row][step] = active;
    }
  }

  /**
   * Setting the octave for the instruments
   *
   * @param {int} octave [the octave]
   */
  setOctave(octave, idNum) {
    if (octave !== this.currentOctaves[idNum]) {
      this.currentOctaves[idNum] = octave;
      this.tracks[idNum].instrument.settings.octave = this.currentOctaves[idNum];
    }
  }

  /**
   * Set preset on pack by ID
   *
   * @param {number} presetId [preset number]
   * @param {number} packNum  [pack number]
   */
  setPresetById(presetId, packNum) {
    let presetName = Object.keys(Presets.names)[presetId];
    if (presetName) {
      this.setPreset(presetName, packNum);
    }
  }

  /**
   * Mute a track
   * @param  {number} instrumentId [id of instrument to mute]
   */
  muteTrack(instrumentId) {
    this.mutedTracks[instrumentId] = true;
  }

  /**
   * Unmute a track
   * @param  {number} instrumentId [id of instrument to unmute]
   */
  unmuteTrack(instrumentId) {
    this.mutedTracks[instrumentId] = false;
  }

  /**
   * Set preset on a pack
   *
   * @param {string} presetName [name of the preset]
   * @param {number} packNum    [number of pack]
   */
  setPreset(presetName, packNum) {
    let presets = Presets.allPresets[Presets.names[presetName]];
    if (presets && presetName !== this.globalPreset) {
      this.globalPreset = presetName;
      this.tracks[packNum].instrument.setPreset(presets[this.tracks[packNum].name]);
    }
  }

  /**
   * Update the global preset by id
   *
   * @param {Number} presetId [ID of the preset to change to]
   */
  setGlobalPresetById(presetId) {
    let presetName = Object.keys(Presets.names)[presetId];
    if (presetName) {
      this.setGlobalPreset(presetName);
    }
  }

  /**
   * Update the global preset by name
   *
   * @param {String} presetName [Name of the preset]
   */
  setGlobalPreset(presetName) {
    let presets = Presets.allPresets[Presets.names[presetName]];
    if (presets && presetName !== this.globalPreset) {
      this.globalPreset = presetName;
      this.tracks.forEach((track,index) => {
        track.instrument.setPreset(presets[track.name])
      });
    }
  }

  /**
   * Toggle the effect for all channels
   * 0: filter, 1: reverb, 2: delay
   *
   * @param  {number} id [id of the filter]
   */
  toggleMasterEffect(id) {
    const active = this.masterEffects[id] = !this.masterEffects[id]
    if (id === 0) {
      this.tracks.forEach((track, index) => {
        track.instrument.toggleMasterEffect(id, active);
      });
    }else if (id === 1) {
      MasterChannel.setReverb(active ? 1 : 0.0)
    }else if (id === 2) {
      MasterChannel.setDelay(active ? 1 : 0.0)
    }
  }

  /**
   * Change master effect
   * 0: filter, 1: reverb, 2: delay
   *
   * @param  {number} id  [id of the effect]
   * @param  {number} val [value of the effect]
   */
  changeMasterEffect(id, val) {
    if (id === 0) {
      this.tracks.forEach((track, index) => {
        track.instrument.setMasterFilterFrequency(val, 0);
      });
    }else if (id === 1) {
      MasterChannel.setReverb(val, 0.1)
    }else if (id === 2) {
      MasterChannel.setDelay(val, 0.1)
    }
  }

  /**
   * Return the master effect by id
   *
   * @param  {number} id [pack id]
   * @return {bool}      [whether master effects are on or not]
   */
  getMasterEffect(id) {
    return this.masterEffects[id];
  }

  /**
   * Start the transport
   */
  startTransport() {
    Tone.Transport.start();
  }

  /**
   * Stop the transport
   */
  stopTransport() {
    Tone.Transport.stop();
  }

  /**
   * Record functionality
   * @return {[type]} [description]
   */
  record(setIsPlaying, setRecording, setIsRecording, setShowDownload) {
    Tone.Transport.pause();
    Tone.Transport.position = Tone.Time(0).toBarsBeatsSixteenths();
    this.downloading = true;
    this.sequencerStep = 0;
    setTimeout(() => {
      setIsPlaying(true);
      const recorder = new Tone.Recorder();
      Tone.Transport.start();
      Tone.Destination.connect(recorder);
      recorder.start();
      setTimeout(async () => {
        const recording = await recorder.stop();
        setRecording(recording);
        Tone.Transport.stop();
        setIsPlaying(false);
        setIsRecording(false);
        setShowDownload(true);
        this.downloading = false;
      }, (60/Tone.Transport.bpm.value) * 1000 * 16);
    }, 1000);
  }

  /**
   * Start recording
   */
  startRecording() {
    this.recorder = new Tone.Recorder();
    Tone.Destination.connect(this.recorder);
    this.recorder.start();
  }

  /**
   * Stop recording
   *
   * @return {promise} [recording promise]
   */
  stopRecording() {
    return new Promise(async (resolve, reject) => {
      const recording = await this.recorder.stop();
      resolve('OK');
      return recording;
    });
  }

  /**
   * Set swing
   *
   * @param {number} val [swing value]
   */
  setSwing(val) {
    Tone.Transport.swing = val;
  }

  /**
   * Get the beats per minute
   *
   * @return {number} [beats per minute]
   */
  getBpm() {
    return Math.floor(Math.round(parseFloat(Tone.Transport.bpm.value, 10)));
  }

  /**
   * Set the beats per minute
   *
   * @param {number} bpm [beats per minute]
   */
  setBpm(bpm) {
    Tone.Transport.bpm.value = parseFloat(bpm);
  }

  /**
   * Set the root (key)
   *
   * @param {number} id [the root id (see helpers -> roots for translation to keys)]
   */
  setRoot(id) {
    this.currentRoot = id;
  }

  /**
   * Get the root (key)
   *
   * @return {number} [the root]
   */
  getRoot() {
    return this.currentRoot;
  }

  /**
   * Set the volume
   *
   * @param {number} val [volume level]
   */
  setVolume(val) {
    MasterChannel.setVolume(val)
  }

  /**
   * Get the note to play
   *
   * @param  {number} instrumentId [the id of the instrument]
   * @param  {number} id           [note id]
   * @return {string}              [the note to play]
   */
  getNoteToPlay(instrumentId, id) {
    if (instrumentId === 1) {
      let samplerScale = [
        "C3",
        "C#3",
        "D3",
        "D#3",
        "E3",
        "F3",
        "F#3",
        "G3",
        "G#3",
        "A3",
        "A#3",
        "B3",
        "C4"
      ];
      let note = samplerScale[id];
      return note;
    } else {
      let note = Scales.getNote(this.currentScale, this.currentRoot, id);
      return note;
    }
  }
}

export default MainController;
